import { DocumentTypology } from "@/app/shared/document-data/config/consts"
import { formatDate } from "@/lib"
import { getTaxCode } from "@/lib/fiscal-code"
import { PartialBonus } from "@/services/get-bonus"
import { CheckPdvActivationResponse } from "@/services/get-check-pdv-activation"
import { FastFillRetrieveDataResponse } from "@/services/get-fast-fill-retrieve-data"
import { isOk } from "@pacote/result"
import CodiceFiscale from "codice-fiscale-js"
import { create } from "zustand"

type SignupStepsStoreData = {
  //codice-fiscale
  name: string
  surname: string
  fiscalCode: CodiceFiscale | null
  //contatti
  prefix: string
  phoneNumber: string
  email: string
  birthDate: string
  //residenza
  country: string
  countryZipCode: string
  city: string
  address: string
  CAP: string
  sameDomicile: boolean
  domicileCountry: string
  domicileCountryZipCode: string
  domicileCity: string
  domicileAddress: string
  domicileCAP: string
  //dati di accesso
  username: string
  password: string
  //bonus
  bonusCode: string
  bonus: PartialBonus | null
  //ocr
  document: {
    documentType: (typeof DocumentTypology)[keyof typeof DocumentTypology]
    documentNumber: string
    releaseDate: string
    expiryDate: string
    releasedFrom: string
    releasedPlace: string
  }
  //limiti di ricarica
  rechargeLimit: number | null
  //termini e condizioni
  flagContract: boolean
  flagTermsAndConditions: boolean
  flagPrivacy: boolean
  flagNewsLetter: string
  flagEmail: boolean
  flagSms: boolean
  flagCall: boolean
  flagProfile: string
  //loyalty
  loyalty: {
    lotteryReceipt: {
      playCode?: string | null
      flagPdv: string
    }
    sisalBonusPoints: string | number
    gameCodeExist: boolean
    sgadId: number
  }
  //rest
  //pdv
  pdvCode?: { accountCode: string; oldPin: string }
  gender?: string
  codiceRice?: string
  emailVerified: boolean
  phoneVerified: boolean
  documentVerified: boolean
  //adm
  admBirthInfo?: {
    birthDate: string
    cityFiscalCode: string
    countryFiscalCode: string
    provinceCode: string
    cityId: string
  } | null
  //otp
  jwt?: string | null
  //otp
  emailToVerify: string
}

type SignupStepsStore = {
  data: SignupStepsStoreData
  setData: (data: Partial<SignupStepsStoreData>) => void
  setOcrData: (data: FastFillRetrieveDataResponse["payload"] & {}) => void
  setPdvData: (
    data: CheckPdvActivationResponse["pdvInfo"],
    oldPin: string,
  ) => void
  reset: () => void
}

const defaultSignupStepsStoreValue = {
  name: "",
  surname: "",
  fiscalCode: null,
  prefix: "39",
  phoneNumber: "",
  email: "",
  emailToVerify: "", // per la dialog email-otp-dialog
  birthDate: "",
  CAP: "",
  address: "",
  city: "",
  country: "ITALIA",
  countryZipCode: "",
  domicileAddress: "",
  domicileCAP: "",
  domicileCity: "",
  domicileCountry: "ITALIA",
  domicileCountryZipCode: "",
  sameDomicile: true,
  username: "",
  password: "",
  bonusCode: "",
  bonus: null,
  document: {
    documentType: "10" as const,
    documentNumber: "",
    releaseDate: "",
    expiryDate: "",
    releasedFrom: "1",
    releasedPlace: "",
  },
  rechargeLimit: null,
  flagContract: false,
  flagTermsAndConditions: false,
  flagPrivacy: false,
  flagNewsLetter: "",
  flagEmail: false,
  flagSms: false,
  flagCall: false,
  flagProfile: "",
  jumioOcrToken: null,
  loyalty: {
    lotteryReceipt: {
      playCode: "",
      flagPdv: "0",
    },
    sisalBonusPoints: "",
    gameCodeExist: false,
    sgadId: -1,
  },
  codiceRice: "",
  gender: "",
  emailVerified: false,
  phoneVerified: false,
  documentVerified: false,
  jwt: null,
  admBirthInfo: null,
}
// {
//   name: "Franco",
//   surname: "Franchigia",
//   fiscalCode: new CodiceFiscale("FPSGRK33L20E328J"),
//   prefix: "39",
//   phoneNumber: "3216549873",
//   email: "",
//   birthDate: "12/12/2000",
//   CAP: "00048",
//   address: "Via Delle Franchige 9",
//   city: "ANZIO (RM)",
//   country: "ITALIA",
//   countryZipCode: "Z000",
//   domicileAddress: "Circolo Panificio 87",
//   domicileCAP: "00048",
//   domicileCity: "Bagdad",
//   domicileCountry: "AFGHANISTAN",
//   domicileCountryZipCode: "Z200",
//   sameDomicile: false,
//   username: "Franchigia96",
//   password: "La.panna.3.bella-",
//   bonusCode: "",
//   bonus: null,
//   document: {
//     documentType: "10",
//     documentNumber: "SSARER4466543",
//     releaseDate: "12/12/2022",
//     expiryDate: "12/12/2025",
//     releasedFrom: "1",
//     releasedPlace: "ABANO TERME (PD)",
//   },
//   rechargeLimit: null,
//   flagContract: true,
//   flagTermsAndConditions: true,
//   flagPrivacy: true,
//   flagNewsLetter: "",
//   flagEmail: false,
//   flagSms: false,
//   flagCall: false,
//   flagProfile: "",
//   jumioAccountId:null,
// emailVerified:false,
// phoneVerified:false,
// documentVerified:false,
// },

const useSignupStepsStore = create<SignupStepsStore>((set) => ({
  data: defaultSignupStepsStoreValue,
  setData: (data) => {
    set((state) => {
      state.data = { ...state.data, ...data }
      return state
    })
  },
  setOcrData: (data) => {
    set((state) => {
      const sameDomicile =
        data?.state?.description === data?.domicileState?.description

      const fiscalCode = getTaxCodeString(data.fiscalCode)

      state.data = {
        ...state.data,
        name: data.name,
        surname: data.familyName,
        ...(data.gender ? { gender: data.gender } : {}),
        birthDate: data.birthDate,
        document: {
          ...state.data.document,
          documentType:
            `${data.documentType}` as SignupStepsStoreData["document"]["documentType"],
          releasedFrom: `${data.releasedByEntity}`,
          ...(data.releaseDate ? { releaseDate: data.releaseDate } : {}),
          ...(data.expirationDate ? { expiryDate: data.expirationDate } : {}),
          ...(data.number ? { documentNumber: data.number } : {}),
          ...(data.documentTown
            ? {
                releasedPlace: `${data.documentTown}${data.documentProvinceCode ? ` (${data.documentProvinceCode})` : ""}`,
              }
            : {}),
        },
        ...(fiscalCode ? { fiscalCode } : {}),
        ...(data.cap ? { CAP: data.cap } : {}),
        ...(data.city
          ? {
              city: `${data.city}${data.province ? ` (${data.province})` : ""}`,
            }
          : {}),
        ...(data.address
          ? {
              address: data.address.substring(0, 35),
              domicileAddress: data.address.substring(0, 35),
            }
          : {}),
        ...(data.state
          ? {
              country: data.state.description,
              countryZipCode: data.state.fiscalCode,
            }
          : {}),
        sameDomicile,
        ...(data.domicileCap ? { domicileCAP: data.domicileCap } : {}),
        ...(data.domicileCity
          ? {
              domicileCity: `${data.domicileCity}${data.domicileProvince ? ` (${data.domicileProvince})` : ""}`,
            }
          : {}),
        domicileCountry: data.domicileState.description,
        domicileCountryZipCode: data.domicileState.fiscalCode,
      }
      return state
    })
  },
  setPdvData: (data, accountPin) => {
    set((state) => {
      state.data = {
        ...state.data,

        name: data.name,
        surname: data.surname,
        fiscalCode: getTaxCodeString(data.fiscalCode),
        phoneNumber: data.phoneNumber,
        email: data.email,
        birthDate: formatDate(new Date(data.birthDate)),
        gender: data.gender,
        username: data.userName,
        pdvCode: { accountCode: data.accountCode, oldPin: accountPin },
        emailVerified: true,
        phoneVerified: true,
        codiceRice: data.codiceRice,
      }
      return state
    })
  },
  reset: () => set({ data: defaultSignupStepsStoreValue }),
}))

function getTaxCodeString(fiscalCode?: string | null) {
  if (!fiscalCode) return null
  const taxCodeResult = getTaxCode(fiscalCode)
  return isOk(taxCodeResult) ? taxCodeResult.value : null
}

export default useSignupStepsStore
export { defaultSignupStepsStoreValue }
